export function addMultipleListeners(element, events, handler, useCapture = false, args = []) {
	if (!element) {
		return;
	}

	if (!Array.isArray(events)) {
		throw new TypeError('addMultipleListeners: please supply an array of event strings (like ["click", "mouseover"])');
	}

	const handlerFn = (e) => handler(e, Array.isArray(args) ? args : []);

	events.forEach(event => {
		element.addEventListener(event, handlerFn, useCapture);
	});
}


// ------------------------------------------------------ //
// Create url
// ------------------------------------------------------ //

export function slugify(string) {
	const map = {
		'à': 'a', 'á': 'a', 'â': 'a', 'ä': 'a', 'æ': 'a', 'ã': 'a', 'å': 'a', 'ā': 'a', 'ă': 'a', 'ą': 'a',
		'ç': 'c', 'ć': 'c', 'č': 'c', 'đ': 'd', 'ď': 'd',
		'è': 'e', 'é': 'e', 'ê': 'e', 'ë': 'e', 'ē': 'e', 'ė': 'e', 'ę': 'e', 'ě': 'e',
		'ğ': 'g', 'ǵ': 'g',
		'ḧ': 'h',
		'î': 'i', 'ï': 'i', 'í': 'i', 'ī': 'i', 'į': 'i', 'ì': 'i',
		'ł': 'l',
		'ḿ': 'm',
		'ñ': 'n', 'ń': 'n', 'ǹ': 'n', 'ň': 'n',
		'ô': 'o', 'ö': 'o', 'ò': 'o', 'ó': 'o', 'œ': 'o', 'ø': 'o', 'ō': 'o', 'õ': 'o', 'ő': 'o',
		'ṕ': 'p',
		'ŕ': 'r', 'ř': 'r',
		'ß': 's', 'ś': 's', 'š': 's', 'ş': 's', 'ș': 's',
		'ť': 't', 'ț': 't',
		'û': 'u', 'ü': 'u', 'ù': 'u', 'ú': 'u', 'ū': 'u', 'ǘ': 'u', 'ů': 'u', 'ű': 'u', 'ų': 'u',
		'ẃ': 'w',
		'ẍ': 'x',
		'ÿ': 'y', 'ý': 'y',
		'ž': 'z', 'ź': 'z', 'ż': 'z',
		'·': '-', '/': '-', '_': '-', ',': '-', ':': '-', ';': '-'
	};

	return string.toString().toLowerCase()
		.replace(/\s+/g, '-') // Replace spaces with -
		.replace(/[àáâäæãåāăąçćčđďèéêëēėęěğǵḧîïíīįìłḿñńǹňôöòóœøōõőṕŕřßśšşșťțûüùúūǘůűųẃẍÿýžźż·/_,:;]/g, match => map[match]) // Replace special characters
		.replace(/&/g, '-and-') // Replace & with 'and'
		.replace(/[^\w\-]+/g, '') // Remove all non-word characters
		.replace(/\-\-+/g, '-') // Replace multiple - with single -
		.replace(/^-+/, '') // Trim - from start of text
		.replace(/-+$/, ''); // Trim - from end of text
}

// ------------------------------------------------------ //
// ajaxGet
// ------------------------------------------------------ //

export async function ajaxGet(url, callback, errorCallback) {
	try {
		const response = await fetch(url);

		if (response.ok) {
			const contentType = response.headers.get('Content-Type');
			let responseData;

			if (contentType && contentType.includes('application/json')) {
				responseData = await response.json();
			} else {
				responseData = await response.text();
			}

			callback(responseData);
		} else {
			throw new Error(response.statusText);
		}
	} catch (error) {
		if (errorCallback) {
			errorCallback(error);
		} else {
			console.error('There was a connection error:', error);
		}
	}
}

// ------------------------------------------------------ //
// Send form
// ------------------------------------------------------ //

export async function ajaxPost(form, callback) {
	const url = form.action;
	const data = serialize(form);

	if (!url) {
		console.error('Form is missing an action URL.');
		return;
	}

	try {
		const response = await fetch(url, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/x-www-form-urlencoded',
			},
			body: data,
		});

		if (response.ok) {
			const contentType = response.headers.get('Content-Type');
			let responseData;

			if (contentType && contentType.includes('application/json')) {
				responseData = await response.json();
			} else {
				responseData = await response.text();
			}

			callback(responseData);
		} else {
			console.error(`Server returned an error: ${response.status} ${response.statusText}`);
		}
	} catch (error) {
		console.error('Connection error:', error);
	}
}

function serialize(form) {
	const serialized = [];

	for (const field of form.elements) {
		if (!field.name || field.disabled || field.type === 'file' || field.type === 'reset' || field.type === 'submit' || field.type === 'button') continue;

		if (field.type === 'select-multiple') {
			for (const option of field.options) {
				if (option.selected) {
					serialized.push(`${encodeURIComponent(field.name)}=${encodeURIComponent(option.value)}`);
				}
			}
		} else if ((field.type !== 'checkbox' && field.type !== 'radio') || field.checked) {
			serialized.push(`${encodeURIComponent(field.name)}=${encodeURIComponent(field.value)}`);
		}
	}

	return serialized.join('&');
}

export async function loadScript(url, callback) {

	var script = document.createElement("script");

	script.setAttribute('async', '');
	script.setAttribute('defer', '');

	if (script.readyState) { //IE
		script.onreadystatechange = function () {
			if (script.readyState == "loaded" || script.readyState == "complete") {
				script.onreadystatechange = null;
				callback();
			}
		};
	} else { //Others
		script.onload = function () {
			callback();
		};
	}

	script.src = url;
	document.body.appendChild(script);
}

export async function loadCSS(href, before, media) {
	// Arguments explained:
	// `href` is the URL for your CSS file.
	// `before` optionally defines the element we'll use as a reference for injecting our <link>
	// By default, `before` uses the first <script> element in the page.
	// However, since the order in which stylesheets are referenced matters, you might need a more specific location in your document.
	// If so, pass a different reference element to the `before` argument and it'll insert before that instead
	// note: `insertBefore` is used instead of `appendChild`, for safety re: http://www.paulirish.com/2011/surefire-dom-element-insertion/
	var ss = window.document.createElement("link");
	var ref = before || window.document.getElementsByTagName("script")[0];
	var sheets = window.document.styleSheets;
	ss.rel = "stylesheet";
	ss.href = href;
	// temporarily, set media to something non-matching to ensure it'll fetch without blocking render
	ss.media = "only x";
	// inject link
	ref.parentNode.insertBefore(ss, ref);
	// This function sets the link's media back to `all` so that the stylesheet applies once it loads
	// It is designed to poll until document.styleSheets includes the new sheet.
	function toggleMedia() {
		var defined;
		for (var i = 0; i < sheets.length; i++) {
			if (sheets[i].href && sheets[i].href.indexOf(href) > -1) {
				defined = true;
			}
		}
		if (defined) {
			ss.media = media || "all";
		}
		else {
			setTimeout(toggleMedia);
		}
	}

	toggleMedia();
	return ss;
}